/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Page Cashback Control sagas
 *
 * @author Yakov Gushchin
 * @version 1.12.2
 * @since 1.12.0
 */

import { createElement } from 'react';

import sortBy from 'lodash/sortBy';
import { call, put, select } from 'redux-saga/effects';

import { IOptionItemProps } from '../../../components/common/Select/Select';
import { pushGTMAnalytic } from '../../../modules/analytics/GoogleTagManager/GTMActions';
import GTMOperations from '../../../modules/analytics/GoogleTagManager/GTMOperations';
import {
    ACCOUNT_CASHBACK_PRODUCT_LIST,
    CASHBACK_RESERVE,
    CASHBACK_TOGGLE_AUTO_SPENDING,
} from '../../../modules/app/Api';
import { getErrorTranslationFromJson } from '../../../modules/internationalization/helpers/intGetErrorTrnaslation';
import {
    makeSelectPageCashbackTranslation,
    makeSelectTranslationCommon,
} from '../../../modules/internationalization/intSelectors';
import { ICommonTranslation } from '../../../modules/internationalization/types';
import { IPageCashbackTranslation } from '../../../modules/internationalization/types/pages/cashback';
import ModalErrorCommon from '../../../modules/modal/components/ModalError';
import { modalOpen, modalSetStatusAction } from '../../../modules/modal/modalActions';
import { EModalStatus, IModalRenderProps } from '../../../modules/modal/types/modalTypes';
import { requestSaga } from '../../../sagas/requestSaga';
import { stringFormatParams } from '../../../utils/stringFormat';
import {
    cashbackControlGetServiceListFailureAction,
    cashbackControlGetServiceListSuccessAction,
    cashbackReserveFailureAction,
    cashbackReserveSuccessAction,
    cashbackToggleAutoSpendingFailureAction,
    cashbackToggleAutoSpendingSuccessAction,
} from '../actions/cashbackControlActions';
import { makeSelectCashbackSelectCurrent } from '../selectors/cashbackSelectors';
import {
    ICashbackControlAvailableService,
    ICashbackControlConnectedService,
    ICashbackControlService,
    ICashbackServiceListAvailableResponse,
    ICashbackServiceListConnectedResponse,
    ICashbackServiceListItemResponse,
    ICashbackServiceListResponse,
    TCashbackReserveResponse,
    TCashbackReserveStartAction,
    TCashbackToggleAutoSpendingResponse,
    TCashbackToggleAutoSpendingStartAction,
} from '../types/cashbackControlTypes';

/*
* -------------------------------------------
*     Get Product List
* -------------------------------------------
*/

export function* cashbackGetProductList() {
    try {
        const accountSelectCurrent: IOptionItemProps = yield select(makeSelectCashbackSelectCurrent());
        const url = stringFormatParams(ACCOUNT_CASHBACK_PRODUCT_LIST, { clientId: accountSelectCurrent.value });

        const response: ICashbackServiceListResponse = yield call(requestSaga, url, 'GET');
        yield put(cashbackControlGetServiceListSuccessAction({
            controlServiceListAvailable:
                sortBy(response.available.map(mapProductListItemResponseToServiceAvailableItem), 'weight'),
            controlServiceListConnected:
                sortBy(response.connected.map(mapProductListItemResponseToServiceConnectedItem), 'weight'),
        }));
    } catch (e: any) {
        yield put(cashbackControlGetServiceListFailureAction(e));
    }
}

export const mapProductListItemResponseToServiceItem = (
    item: ICashbackServiceListItemResponse,
    index: number,
): ICashbackControlService => ({
    description: item.shortDescription,
    id: index + 1,
    linkMore: item.wwwLink || item.pdfLink,
    loyaltyRewardId: item.loyaltyRewardId,
    title: item.title,
});

export const mapProductListItemResponseToServiceAvailableItem = (
    item: ICashbackServiceListAvailableResponse,
    index: number,
): ICashbackControlAvailableService => ({
    ...mapProductListItemResponseToServiceItem(item, index),
    activationLink: item.activationLink,
});

export const mapProductListItemResponseToServiceConnectedItem = (
    item: ICashbackServiceListConnectedResponse,
    index: number,
): ICashbackControlConnectedService => ({
    ...mapProductListItemResponseToServiceItem(item, index),
    scoreReserved: item.balanceReserved,
});

/*
* -------------------------------------------
*     Reserve scores for the service
* -------------------------------------------
*/

export function* cashbackReserveSaga({ payload }: TCashbackReserveStartAction) {
    try {
        // Set loading status for modal
        yield put(modalSetStatusAction({ status: EModalStatus.processing }));

        const accountSelectCurrent: IOptionItemProps = yield select(makeSelectCashbackSelectCurrent());
        const url = stringFormatParams(CASHBACK_RESERVE, { clientId: accountSelectCurrent.value });

        const response: TCashbackReserveResponse = yield call(requestSaga, url, 'POST', {
            amount: payload.amount,
            loyaltyRewardId: payload.loyaltyRewardId,
        });
        yield put(cashbackReserveSuccessAction(response));
        yield put(modalSetStatusAction({ status: EModalStatus.success }));
    } catch (e) {
        yield put(modalSetStatusAction({ status: EModalStatus.failure }));

        const cashbackTranslation: IPageCashbackTranslation = yield select(makeSelectPageCashbackTranslation());
        const errorMessage = getErrorTranslationFromJson(e, cashbackTranslation.error);
        yield put(cashbackReserveFailureAction(errorMessage));
    }
}

/*
* -------------------------------------------
*     Toggle Auto Spending
* -------------------------------------------
*/

export function* cashbackToggleAutoSpendingSaga({ payload }: TCashbackToggleAutoSpendingStartAction) {
    try {
        yield put(pushGTMAnalytic({
            operation: GTMOperations.cashback.useSliderCashbackControlTab(!payload.isAutoSpendingEnabled),
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        }));
        const accountSelectCurrent: IOptionItemProps = yield select(makeSelectCashbackSelectCurrent());
        const url = stringFormatParams(CASHBACK_TOGGLE_AUTO_SPENDING, {
            clientId: accountSelectCurrent.value,
            state: !payload.isAutoSpendingEnabled,
        });
        const response: TCashbackToggleAutoSpendingResponse = yield call(requestSaga, url, 'PUT');

        yield put(cashbackToggleAutoSpendingSuccessAction({
            isAutoSpendingEnabled: response.autoSpendingBonusesEnabled,
        }));
    } catch (error: any) {
        const translation: ICommonTranslation = yield select(makeSelectTranslationCommon());
        yield put(cashbackToggleAutoSpendingFailureAction(error));
        yield put(modalOpen({
            render: (props: IModalRenderProps) => createElement(ModalErrorCommon, {
                onCloseModal: props.onCloseModal,
                translation: translation.modal,
            }),
        }));
    }
}
